import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/Auth.css';

function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [formData, setFormData] = useState({
    email: '', username: '', full_name: '', password: '', agreed: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.agreed) {
      setError('You must agree to the Terms of Service and Privacy Policy.');
      return;
    }
    setLoading(true);
    try {
      const response = await register(formData);
      const email = response?.data?.email || formData.email;
      navigate(`/verify-email?email=${encodeURIComponent(email)}`);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* ── Sidebar ── */}
      <div className="auth-sidebar">
        <Link to="/" className="auth-sidebar__logo">
          Postrify <span className="auth-sidebar__dot" />
        </Link>
        <div className="auth-sidebar__body">
          <h2 className="auth-sidebar__headline">
            Start growing<br /><span>in minutes.</span>
          </h2>
          <p className="auth-sidebar__desc">
            Create your free account and start generating professional
            social media content for all your brands today.
          </p>
          <ul className="auth-sidebar__features">
            {['No credit card required', 'Multi-brand support', 'AI images + captions', 'Auto-publish to Instagram & Facebook'].map((f) => (
              <li className="auth-sidebar__feature" key={f}>
                <span className="auth-sidebar__feature-icon">✓</span> {f}
              </li>
            ))}
          </ul>
        </div>
        <p className="auth-sidebar__footer">© 2024 Postrify</p>
      </div>

      {/* ── Form Panel ── */}
      <div className="auth-panel">
        <div className="auth-form-wrap">
          <Link to="/" className="auth-form-wrap__back">← Back to home</Link>

          <h1 className="auth-form-wrap__title">Create your account</h1>
          <p className="auth-form-wrap__sub">Start generating AI-powered content today</p>

          {error && <div className="ps-alert ps-alert--error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="ps-field">
              <label className="ps-label" htmlFor="email">Email Address</label>
              <input className="ps-input" type="email" id="email" name="email" required
                placeholder="you@example.com" value={formData.email} onChange={handleChange} />
            </div>

            <div className="ps-field">
              <label className="ps-label" htmlFor="username">Username</label>
              <input className="ps-input" type="text" id="username" name="username" required
                minLength="3" maxLength="50" placeholder="johndoe"
                pattern="[a-zA-Z0-9_-]+" title="Only letters, numbers, hyphens, and underscores"
                value={formData.username} onChange={handleChange} />
            </div>

            <div className="ps-field">
              <label className="ps-label" htmlFor="full_name">
                Full Name{' '}
                <span style={{ fontWeight:400, textTransform:'none', letterSpacing:0, color:'#8b8aab' }}>(Optional)</span>
              </label>
              <input className="ps-input" type="text" id="full_name" name="full_name"
                maxLength="200" placeholder="John Doe"
                value={formData.full_name} onChange={handleChange} />
            </div>

            <div className="ps-field">
              <label className="ps-label" htmlFor="password">Password</label>
              <input className="ps-input" type="password" id="password" name="password"
                required minLength="8" placeholder="Min. 8 characters"
                value={formData.password} onChange={handleChange} />
              <p className="ps-hint">Must be at least 8 characters long.</p>
            </div>

            <div className="ps-field">
              <label className="ps-checkbox-row">
                <input type="checkbox" name="agreed" checked={formData.agreed} onChange={handleChange} required />
                I agree to the{' '}
                <Link to="/terms" target="_blank" style={{ color:'#4f46e5', fontWeight:600 }}>Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" style={{ color:'#4f46e5', fontWeight:600 }}>Privacy Policy</Link>
              </label>
            </div>

            <button type="submit" className="ps-btn ps-btn--primary ps-btn--full ps-btn--lg" disabled={loading}>
              {loading ? 'Creating account…' : 'Create account →'}
            </button>
          </form>

          <p className="auth-bottom">
            Already have an account? <Link to="/login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Register;
